import React, { useState } from 'react';
import { Plus, Trash2, Upload, Edit3, Save } from 'lucide-react';
import api, { formatApiError } from '../../lib/api';
import { useData } from '../../context/DataContext';
import { useToast } from '../../hooks/use-toast';

const empty = { name_en: '', name_hi: '', desc_en: '', desc_hi: '', price: 101, image: '', icon: 'Sparkles' };

export default function SamagriTab() {
  const { samagri, refresh } = useData();
  const { toast } = useToast();
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [show, setShow] = useState(false);

  const openNew = () => { setEditingId(null); setForm(empty); setShow(true); window.scrollTo({ top: 300, behavior: 'smooth' }); };
  const openEdit = (s) => { setEditingId(s.id); setForm({ ...empty, ...s }); setShow(true); window.scrollTo({ top: 300, behavior: 'smooth' }); };

  const del = async (id) => {
    if (!window.confirm('Delete this samagri item?')) return;
    try {
      await api.delete(`/api/samagri/${id}`);
      await refresh();
      toast({ title: 'Samagri removed' });
    } catch (e) { toast({ title: formatApiError(e), variant: 'destructive' }); }
  };

  const upImg = (e) => {
    const f = e.target.files[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => setForm((p) => ({ ...p, image: r.result }));
    r.readAsDataURL(f);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: +form.price };
    try {
      if (editingId) await api.put(`/api/samagri/${editingId}`, payload);
      else await api.post('/api/samagri', payload);
      await refresh();
      toast({ title: editingId ? 'Samagri updated' : 'Samagri added' });
      setShow(false);
    } catch (err) { toast({ title: formatApiError(err), variant: 'destructive' }); }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <p className="text-sm text-nv-muted">Manage samagri / offerings shown at checkout.</p>
        <button data-testid="add-samagri-btn" onClick={openNew} className="btn-orange rounded-full px-4 md:px-5 py-2.5 font-semibold flex items-center gap-2 text-sm">
          <Plus className="w-4 h-4" /> Add Samagri
        </button>
      </div>

      {show && (
        <form onSubmit={save} className="bg-white border border-orange-200 rounded-2xl p-4 md:p-6 mb-6 grid md:grid-cols-2 gap-4 shadow-md">
          <h3 className="md:col-span-2 text-lg font-bold text-orange-800">{editingId ? 'Edit Samagri' : 'New Samagri'}</h3>
          {[
            ['name_en', 'Name (English)', true],
            ['name_hi', 'Name (Hindi)', true],
            ['desc_en', 'Description (English)', false],
            ['desc_hi', 'Description (Hindi)', false],
          ].map(([k, l, req]) => (
            <div key={k}>
              <label className="text-xs font-medium text-stone-600 mb-1 block">{l}</label>
              <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required={req} className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          ))}
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Price (₹)</label>
            <input type="number" min="1" value={form.price} onChange={(e) => setForm({ ...form, price: +e.target.value })} required className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm font-semibold text-orange-700" />
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Fallback Icon</label>
            <select value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm bg-white">
              {['Sparkles', 'Shirt', 'UtensilsCrossed', 'Flame', 'Gem', 'Nut', 'Package', 'Trophy'].map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-stone-600 mb-1 block">Image (URL or upload)</label>
            <div className="flex gap-2">
              <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://..." className="flex-1 border border-orange-200 rounded-lg px-3 py-2 text-sm" />
              <label className="px-3 py-2 rounded-lg border border-orange-300 text-orange-700 text-sm cursor-pointer hover:bg-orange-50 flex items-center gap-1">
                <Upload className="w-4 h-4" /> Upload
                <input type="file" accept="image/*" onChange={upImg} className="hidden" />
              </label>
            </div>
            {form.image && <img src={form.image} alt="" className="mt-2 w-24 h-24 rounded-lg object-cover border border-orange-200" />}
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-3 justify-end pt-3">
            <button type="button" onClick={() => setShow(false)} className="px-5 py-2 rounded-lg border border-stone-300 text-stone-700">Cancel</button>
            <button data-testid="save-samagri-btn" type="submit" className="btn-orange px-6 py-2 rounded-lg font-semibold flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
          </div>
        </form>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="samagri-list">
        {samagri.map((s) => (
          <div key={s.id} className="bg-white border border-nv-gold/20 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition">
            {s.image ? (
              <img src={s.image} alt={s.name_en} className="w-full h-32 object-cover" />
            ) : (
              <div className="w-full h-32 bg-orange-50 flex items-center justify-center text-orange-500">No image</div>
            )}
            <div className="p-4">
              <h4 className="font-semibold text-sm text-nv-wine line-clamp-1">{s.name_en}</h4>
              <p className="text-[11px] text-stone-500 devnag truncate">{s.name_hi}</p>
              <div className="text-orange-700 font-bold my-2">+₹ {s.price}</div>
              <p className="text-[11px] text-stone-600 line-clamp-2 mb-3">{s.desc_en}</p>
              <div className="flex gap-2">
                <button data-testid={`edit-samagri-${s.id}`} onClick={() => openEdit(s)} className="flex-1 border border-orange-300 text-orange-700 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 hover:bg-orange-50"><Edit3 className="w-3 h-3" /> Edit</button>
                <button data-testid={`delete-samagri-${s.id}`} onClick={() => del(s.id)} className="flex-1 border border-red-300 text-red-600 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 hover:bg-red-50"><Trash2 className="w-3 h-3" /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
